import { useState } from 'react';
import { CalendarClock, MapPin, CheckCircle, Loader2, Navigation } from 'lucide-react';
import { apptT, formatAppt } from './apptI18n';

const ResponseActions = ({ t, onRespond, initialMove, compact }) => {
  const [moving, setMoving] = useState(!!initialMove);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const run = async (action) => {
    setBusy(action);
    try { await onRespond(action, note); } finally { setBusy(''); }
  };
  const btn = compact ? 'text-xs px-3 py-1.5' : 'text-sm px-5 py-2.5';
  if (moving) {
    return (
      <div className="space-y-2 mt-3" data-testid="appt-move-form">
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={1000} placeholder={t.notePh}
          className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5FC1]" data-testid="appt-move-note" />
        <div className="flex flex-wrap gap-2">
          <button onClick={() => run('sposta')} disabled={!!busy} className={`${btn} bg-[#283880] hover:bg-[#1f2c66] text-white rounded-lg inline-flex items-center gap-2 transition-colors`} data-testid="appt-move-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t.send}
          </button>
          <button onClick={() => setMoving(false)} className={`${btn} text-slate-500 hover:text-[#283880]`} data-testid="appt-move-cancel">{t.cancel}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3">
      <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-[#283880] mb-2`}>{t.ask}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => run('conferma')} disabled={!!busy} className={`${btn} bg-green-600 hover:bg-green-700 text-white rounded-lg inline-flex items-center gap-2 transition-colors`} data-testid="appt-confirm-btn">
          {busy === 'conferma' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}{t.confirm}
        </button>
        <button onClick={() => setMoving(true)} disabled={!!busy} className={`${btn} border-2 border-[#283880] text-[#283880] hover:bg-[#283880] hover:text-white rounded-lg transition-colors`} data-testid="appt-move-btn">
          {t.move}
        </button>
      </div>
    </div>
  );
};

export const AppointmentCard = ({ appt, lang, onRespond, initialAction, compact = false, error }) => {
  const t = apptT[lang] || apptT.it;
  const [moveAgain, setMoveAgain] = useState(initialAction === 'sposta' && appt.response === 'confermato');
  const done = appt.response && !moveAgain;
  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'} data-testid="appointment-card">
      <p className={`flex items-start gap-2 ${compact ? 'text-xs' : 'text-base'} font-semibold text-[#283880]`} data-testid="appointment-date">
        <CalendarClock className="w-4 h-4 mt-0.5 text-[#C9A227] shrink-0" />{formatAppt(appt.appuntamento, lang)}
      </p>
      {appt.sede && (
        <div className={`${compact ? 'text-xs' : 'text-sm'} text-slate-600 flex items-start gap-2`} data-testid="appointment-sede">
          <MapPin className="w-4 h-4 mt-0.5 text-[#C9A227] shrink-0" />
          <span>{appt.sede.name} – {appt.sede.address}<br />
            <a href={appt.sede.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#283880] underline hover:text-[#C9A227]" data-testid="appointment-map-link">
              <Navigation className="w-3 h-3" />{t.openMap}
            </a>
          </span>
        </div>
      )}
      {done && (
        <div className={`${compact ? 'text-xs' : 'text-sm'} rounded-lg p-3 ${appt.response === 'confermato' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`} data-testid="appointment-response">
          {appt.response === 'confermato' ? t.confirmed : t.moved}
          {appt.response === 'confermato' && (
            <button onClick={() => setMoveAgain(true)} className="block mt-1 underline text-xs text-slate-500" data-testid="appt-change-mind">{t.changeMind}</button>
          )}
        </div>
      )}
      {!done && <ResponseActions t={t} compact={compact} initialMove={moveAgain || initialAction === 'sposta'}
        onRespond={async (a, n) => { await onRespond(a, n); setMoveAgain(false); }} />}
      {error && <p className="text-xs text-red-600" data-testid="appointment-error">{error}</p>}
    </div>
  );
};
